import React, { useState, useEffect } from "react";
import config from "../../Config";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import moment from "moment";
import PrintHeader from "../General/PrintHeader";
import Page from "../General/Page";
import RightsCheck from "../General/RightsCheck";

const PrintReceiptPayment = () => {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));
  const [DefaultFYear, setDefaultFYear] = useState(
    localStorage.getItem("DefaultFYear")
  );
  const [loading, setLoading] = useState(false);
  const [listOfOpening, setListOfOpening] = useState([]);
  const [listOfReceipts, setListOfReceipts] = useState([]);
  const [listOfPayments, setListOfPayments] = useState([]);
  const [listOfClosing, setListOfClosing] = useState([]);
  const [ClosingDebitTotal, setClosingDebitTotal] = useState("");
  const [ClosingCreditTotal, setClosingCreditTotal] = useState("");
  const [isBalanced, setIsBalanced] = useState(null);
  const [balanceDifference, setBalanceDifference] = useState("");
  const [head05ClosingDebit, setHead05ClosingDebit] = useState("");
  const [head05ClosingCredit, setHead05ClosingCredit] = useState("");
  const [AccountLevel, setAccountLevel] = useState("1");
  const [EndDate, setEndDate] = useState("");
  const [showZeroAccounts, setShowZeroAccounts] = useState(false);
  const [showThirdOnly, setShowThirdOnly] = useState(false);

  useEffect(() => {
    document.title = "Print Receipt Payment";
    document.body.classList.add("print-body");
    return () => {
      document.body.classList.remove("print-body");
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paramFYear = params.get("FYear");
    const paramEndDate = params.get("EndDate");
    const paramAccountLevel = params.get("AccountLevel");
    const paramShowZero = params.get("ShowZeroAccounts");
    const paramShowThirdOnly = params.get("ShowThirdOnly");

    if (paramFYear) {
      setDefaultFYear(paramFYear);
    }
    if (paramEndDate) {
      setEndDate(paramEndDate);
    }
    if (paramAccountLevel) {
      setAccountLevel(paramAccountLevel);
    }
    if (paramShowZero !== null) {
      setShowZeroAccounts(
        paramShowZero === "1" || paramShowZero.toLowerCase() === "true"
      );
    }
    if (paramShowThirdOnly !== null) {
      setShowThirdOnly(
        paramShowThirdOnly === "1" || paramShowThirdOnly.toLowerCase() === "true"
      );
    }
  }, []);

  useEffect(() => {
    if (!DefaultFYear) {
      return;
    }
    const fy = parseInt(DefaultFYear, 10);
    if (Number.isNaN(fy)) {
      return;
    }
    const fyStart = moment(`${fy}-04-01`);
    const fyEnd = moment(`${fy + 1}-03-31`);
    const today = moment();
    const defaultEnd = today.isBetween(fyStart, fyEnd, "day", "[]") ? today : fyEnd;
    if (!EndDate) {
      setEndDate(defaultEnd.format(config.date_format_input));
    }
  }, [DefaultFYear]);

  useEffect(() => {
    if (!EndDate || !DefaultFYear) {
      return;
    }
    setLoading(true);
    const fy = parseInt(String(DefaultFYear).trim(), 10);
    const data = {
      AccessKey: AccessKey,
      UserID: UserID,
      MemberType: MemberType,
      FYear: fy,
      EndDate: EndDate,
      AccountLevel: parseInt(String(AccountLevel).trim(), 10) || 1,
      ShowZeroAccounts: showZeroAccounts,
      ShowThirdOnly: showThirdOnly,
    };

    var api_config = {
      method: "post",
      url: config.base_url + "Accounts/GetReceiptPayment",
      headers: {
        "Content-Type": "application/json",
      },
      data: data,
    };

    axios(api_config)
      .then(function (response) {
        if (response.data.status_code === 1) {
          setListOfOpening(response.data.ListofOpening || []);
          setListOfReceipts(response.data.ListofReceipts || []);
          setListOfPayments(response.data.ListofPayments || []);
          setListOfClosing(response.data.ListofClosing || []);
          setClosingDebitTotal(response.data.ClosingDebitTotal || "");
          setClosingCreditTotal(response.data.ClosingCreditTotal || "");
          setIsBalanced(response.data.IsBalanced === true);
          setBalanceDifference(response.data.BalanceDifference || "");
          setHead05ClosingDebit(response.data.Head05ClosingDebit || "");
          setHead05ClosingCredit(response.data.Head05ClosingCredit || "");
        } else {
          setListOfOpening([]);
          setListOfReceipts([]);
          setListOfPayments([]);
          setListOfClosing([]);
          setClosingDebitTotal("");
          setClosingCreditTotal("");
          setIsBalanced(null);
          setBalanceDifference("");
          setHead05ClosingDebit("");
          setHead05ClosingCredit("");
        }
      })
      .catch(function () {})
      .finally(function () {
        setLoading(false);
      });
  }, [DefaultFYear, AccountLevel, EndDate, showZeroAccounts, showThirdOnly]);

  const renderRows = (items, isOpening) => {
    if (!items || items.length === 0) {
      return (
        <tr>
          <td colSpan="4">
            <h4
              className="mt-3"
              style={{ fontSize: "15px", color: "rgba(0,0,0,0.6)" }}
            >
              No Data Found
            </h4>
          </td>
        </tr>
      );
    }
    return items.map((item, index) => {
      const code = item.Selected_Account?.Account_code || "";
      const rowStyle = code.length === 2
        ? { backgroundColor: "#fddede" }
        : code.length === 5
          ? { backgroundColor: "#d9f7fe" }
          : undefined;
      return (
        <tr key={index} style={rowStyle}>
          <td>{item.Selected_Account.Account_code}</td>
          <td>{item.Selected_Account.Account_desc}</td>
          {isOpening ? (
            <>
              <td className="text-end">{item.OpeningDebit}</td>
              <td className="text-end">{item.OpeningCredit}</td>
            </>
          ) : (
            <>
              <td className="text-end">{item.ClosingCredit}</td>
              <td className="text-end">{item.ClosingDebit}</td>
            </>
          )}
        </tr>
      );
    });
  };

  return (
    <>
      <RightsCheck />
      <Page orientation="portrait" size="a4">
        <PrintHeader title="Receipt Payment" />
        <p className="small text-muted mb-2">
          Account heads 04, 05, 06, 07 only. Totals from 3rd-level accounts.
        </p>
        <table className="table table-sm table-bordered table-print">
          <tbody>
            {loading ? (
              <tr>
                <td className="text-center" colSpan="4">
                  <Spinner animation="border" role="status"></Spinner>
                </td>
              </tr>
            ) : (
              <>
                <tr>
                  <th>Account Code</th>
                  <th>Account Name</th>
                  <th colSpan="2" className="text-center">Closing</th>
                </tr>
                <tr>
                  <th></th>
                  <th></th>
                  <th className="text-center">Debit</th>
                  <th className="text-center">Credit</th>
                </tr>
                <tr>
                  <td colSpan="4"><h5>OPENING BALANCE</h5></td>
                </tr>
                {renderRows(listOfOpening, true)}
                <tr>
                  <td colSpan="4"><h5>RECEIPTS</h5></td>
                </tr>
                {renderRows(listOfReceipts, false)}
                <tr>
                  <td colSpan="4"><h5>PAYMENTS</h5></td>
                </tr>
                {renderRows(listOfPayments, false)}
                <tr>
                  <td colSpan="4"><h5>CLOSING BALANCE</h5></td>
                </tr>
                {renderRows(listOfClosing, false)}
                <tr>
                  <td colSpan="2" className="text-end">Totals (3rd level)</td>
                  <td className="text-end">{ClosingDebitTotal}</td>
                  <td className="text-end">{ClosingCreditTotal}</td>
                </tr>
                {isBalanced !== null && (
                  <tr>
                    <td colSpan="4">
                      {isBalanced
                        ? "Balanced: Total Debit equals Total Credit."
                        : `Not balanced. Difference (Debit − Credit): ${balanceDifference}`}
                    </td>
                  </tr>
                )}
                {(head05ClosingDebit || head05ClosingCredit) && (
                  <tr>
                    <td colSpan="4" className="small">
                      Head 05 closing — Debit: {head05ClosingDebit}, Credit: {head05ClosingCredit}
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </Page>
    </>
  );
};

export default PrintReceiptPayment;
